import React, { useRef, useState } from "react";
import { ImagePlus, X, Loader2 } from "lucide-react";

/**
 * ImageUploadButton — converts a selected file to base64 data URL with
 * client-side resize/compress to fit under ~1.5MB. Calls onPicked(dataUrl).
 * Use:
 *   const [img, setImg] = useState(null);
 *   <ImageUploadButton onPicked={setImg} />
 *   {img && <ImagePreview src={img} onClear={() => setImg(null)} />}
 */
export default function ImageUploadButton({ onPicked, disabled = false, testid = "image-upload-btn" }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);

  const compress = (file) => new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();
    reader.onload = () => { img.src = reader.result; };
    reader.onerror = reject;
    img.onload = () => {
      // Resize to max 1600 on the long edge
      const MAX = 1600;
      let { width: w, height: h } = img;
      if (w > MAX || h > MAX) {
        const ratio = Math.min(MAX / w, MAX / h);
        w = Math.round(w * ratio);
        h = Math.round(h * ratio);
      }
      const canvas = document.createElement("canvas");
      canvas.width = w; canvas.height = h;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, w, h);
      // Try JPEG at 0.85 quality (smaller); fall back to original mime if too small
      const dataUrl = canvas.toDataURL("image/jpeg", 0.85);
      resolve(dataUrl);
    };
    img.onerror = reject;
    reader.readAsDataURL(file);
  });

  const onChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      alert("Vyber prosím obrázok (JPG / PNG / WEBP).");
      e.target.value = "";
      return;
    }
    setBusy(true);
    try {
      const dataUrl = await compress(file);
      if (dataUrl.length > 2_200_000) {
        alert("Obrázok je príliš veľký aj po komprimácii — skús menší.");
      } else {
        onPicked(dataUrl);
      }
    } catch (err) {
      console.error(err);
      alert("Nepodarilo sa načítať obrázok.");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  return (
    <>
      <input
        ref={ref}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={onChange}
        data-testid={`${testid}-input`}
      />
      <button
        type="button"
        onClick={() => ref.current?.click()}
        disabled={disabled || busy}
        data-testid={testid}
        title="Vložiť obrázok"
        className="p-2 text-zinc-400 hover:text-gold-400 disabled:opacity-50 transition-colors"
      >
        {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <ImagePlus className="w-5 h-5" />}
      </button>
    </>
  );
}

export function ImageThumbPreview({ src, onClear }) {
  return (
    <div className="relative inline-block" data-testid="image-preview">
      <img src={src} alt="" className="h-20 w-auto object-cover border border-gold-700" />
      <button
        type="button"
        onClick={onClear}
        data-testid="image-clear-btn"
        className="absolute -top-2 -right-2 w-6 h-6 bg-zinc-900 border border-zinc-700 hover:border-red-600 hover:text-red-400 flex items-center justify-center"
      >
        <X className="w-3 h-3" />
      </button>
    </div>
  );
}

export function ChatImage({ src }) {
  const [open, setOpen] = useState(false);
  if (!src) return null;
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="chat-image-thumb"
        className="block mt-2 max-w-xs"
      >
        <img
          src={src}
          alt=""
          className="max-h-64 w-auto border border-zinc-700 hover:border-gold-500 transition-colors cursor-zoom-in"
        />
      </button>
      {open && (
        <div
          onClick={() => setOpen(false)}
          data-testid="chat-image-lightbox"
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4 cursor-zoom-out"
        >
          <img src={src} alt="" className="max-h-[92vh] max-w-[92vw] object-contain" />
          <button
            onClick={(e) => { e.stopPropagation(); setOpen(false); }}
            className="absolute top-4 right-4 w-10 h-10 border border-zinc-700 bg-zinc-900 flex items-center justify-center hover:border-gold-500"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      )}
    </>
  );
}
